import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, AreaChart,
} from 'recharts';
import {
    ArrowLeft, Truck, Gauge, Fuel, AlertTriangle, TrendingUp,
    TrendingDown, Calendar, MapPin, User, Info, Clock, Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselVehiculoDetallePage() {
    const { vehiculoId } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useDiesel();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        loadData();
    }, [vehiculoId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/diesel/vehiculos/${vehiculoId}/historial`, { headers });
            if (res.ok) {
                setData(await res.json());
            } else {
                toast.error('Vehiculo no encontrado');
                navigate('/diesel/vehiculos');
            }
        } catch {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DieselLayout>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
                    </div>
                    <Skeleton className="h-80" />
                </div>
            </DieselLayout>
        );
    }

    if (!data) return null;

    const { vehiculo, cargas, alertas, estadisticas, tendencia_mensual } = data;
    const rendFabricante = vehiculo?.modelo_info?.rendimiento_fabricante;

    const chartCargas = cargas
        .filter(c => c.rendimiento_calculado)
        .map(c => ({
            fecha: c.fecha_hora?.slice(0, 10),
            rendimiento: c.rendimiento_calculado,
            litros: c.litros_cargados,
            km: c.km_recorridos,
            fabricante: rendFabricante,
        }));

    const chartConsumo = cargas
        .filter(c => !c.es_carga_inicial)
        .slice(-30)
        .map(c => ({
            fecha: c.fecha_hora?.slice(5, 10),
            litros: c.litros_cargados,
            km: c.km_recorridos || 0,
        }));

    const alertasPendientes = alertas.filter(a => a.estado === 'pendiente');

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/diesel/vehiculos')} data-testid="back-to-vehiculos">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Volver
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight flex items-center gap-3" data-testid="vehiculo-detalle-title">
                            <Truck className="h-8 w-8" style={{ color: 'var(--diesel-primary)' }} />
                            {vehiculo.patente}
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {vehiculo.modelo_info
                                ? `${vehiculo.modelo_info.marca} ${vehiculo.modelo_info.nombre}${vehiculo.año ? ` (${vehiculo.año})` : ''}`
                                : 'Sin modelo asignado'}
                        </p>
                    </div>
                    {vehiculo.estado !== 'activo' && (
                        <Badge variant="destructive" className="text-sm">{vehiculo.estado}</Badge>
                    )}
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="border-2" data-testid="kpi-total-cargas">
                        <CardContent className="p-4 text-center">
                            <Fuel className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-black">{estadisticas.total_cargas}</p>
                            <p className="text-xs text-muted-foreground uppercase">Cargas</p>
                        </CardContent>
                    </Card>
                    <Card className="border-2" data-testid="kpi-rendimiento">
                        <CardContent className="p-4 text-center">
                            <Gauge className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-black">
                                {estadisticas.rendimiento_promedio ? `${estadisticas.rendimiento_promedio}` : '-'}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase">Rend. Prom (km/l)</p>
                        </CardContent>
                    </Card>
                    <Card className="border-2" data-testid="kpi-litros">
                        <CardContent className="p-4 text-center">
                            <Fuel className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-black">{estadisticas.total_litros.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground uppercase">Litros Totales</p>
                        </CardContent>
                    </Card>
                    <Card className="border-2" data-testid="kpi-km">
                        <CardContent className="p-4 text-center">
                            <TrendingUp className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                            <p className="text-2xl font-black">{estadisticas.total_km.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground uppercase">KM Recorridos</p>
                        </CardContent>
                    </Card>
                    <Card className={`border-2 ${estadisticas.alertas_pendientes > 0 ? 'border-red-500/30 bg-red-500/5' : ''}`} data-testid="kpi-alertas">
                        <CardContent className="p-4 text-center">
                            <AlertTriangle className={`h-5 w-5 mx-auto mb-1 ${estadisticas.alertas_pendientes > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                            <p className={`text-2xl font-black ${estadisticas.alertas_pendientes > 0 ? 'text-red-600' : ''}`}>
                                {estadisticas.alertas_pendientes}
                            </p>
                            <p className="text-xs text-muted-foreground uppercase">Alertas Pend.</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Info del vehiculo */}
                <div className="grid md:grid-cols-3 gap-4">
                    <Card className="border-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm uppercase text-muted-foreground flex items-center gap-2">
                                <Info className="h-4 w-4" /> Datos del Vehiculo
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">Patente:</span><span className="font-mono font-bold">{vehiculo.patente}</span></div>
                            {vehiculo.modelo_info && (
                                <>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Marca:</span><span>{vehiculo.modelo_info.marca}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Modelo:</span><span>{vehiculo.modelo_info.nombre}</span></div>
                                    {rendFabricante && (
                                        <div className="flex justify-between"><span className="text-muted-foreground">Rend. Fabricante:</span><span className="font-bold">{rendFabricante} km/l</span></div>
                                    )}
                                </>
                            )}
                            {vehiculo.año && <div className="flex justify-between"><span className="text-muted-foreground">Ano:</span><span>{vehiculo.año}</span></div>}
                            {vehiculo.capacidad_estanque && <div className="flex justify-between"><span className="text-muted-foreground">Estanque:</span><span>{vehiculo.capacidad_estanque} L</span></div>}
                            {vehiculo.color && <div className="flex justify-between"><span className="text-muted-foreground">Color:</span><span>{vehiculo.color}</span></div>}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm uppercase text-muted-foreground flex items-center gap-2">
                                <User className="h-4 w-4" /> Conductores Asignados
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {vehiculo.conductores_info?.length > 0 ? (
                                <div className="space-y-3">
                                    {vehiculo.conductores_info.map((c, i) => (
                                        <div key={i} className="space-y-1">
                                            <p className="font-bold">{c.nombre}</p>
                                            {c.rut && <p className="text-sm text-muted-foreground">RUT: {c.rut}</p>}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-sm">Sin conductores asignados</p>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-2">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm uppercase text-muted-foreground flex items-center gap-2">
                                <Activity className="h-4 w-4" /> Rendimiento vs Fabricante
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {estadisticas.rendimiento_promedio && rendFabricante ? (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        {estadisticas.rendimiento_promedio >= rendFabricante ? (
                                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                                        ) : (
                                            <TrendingDown className="h-5 w-5 text-red-500" />
                                        )}
                                        <span className={`text-2xl font-black ${estadisticas.rendimiento_promedio >= rendFabricante ? 'text-emerald-600' : 'text-red-600'}`}>
                                            {((estadisticas.rendimiento_promedio / rendFabricante) * 100 - 100).toFixed(1)}%
                                        </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Promedio: {estadisticas.rendimiento_promedio} km/l vs Ref: {rendFabricante} km/l
                                    </p>
                                </div>
                            ) : (
                                <p className="text-muted-foreground text-sm">Datos insuficientes</p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Tabs: Graficos, Cargas, Alertas */}
                <Tabs defaultValue="graficos" className="space-y-4">
                    <TabsList data-testid="vehiculo-detalle-tabs">
                        <TabsTrigger value="graficos">Graficos</TabsTrigger>
                        <TabsTrigger value="cargas">Historial Cargas</TabsTrigger>
                        <TabsTrigger value="alertas">
                            Alertas
                            {alertasPendientes.length > 0 && (
                                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 text-xs flex items-center justify-center rounded-full">
                                    {alertasPendientes.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab: Graficos */}
                    <TabsContent value="graficos" className="space-y-6">
                        {chartCargas.length > 1 ? (
                            <>
                                {/* Grafico de Rendimiento */}
                                <Card className="border-2" data-testid="chart-rendimiento">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Gauge className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                            Rendimiento por Carga (km/l)
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-72">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <AreaChart data={chartCargas}>
                                                    <defs>
                                                        <linearGradient id="rendGrad" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="5%" stopColor="var(--diesel-primary)" stopOpacity={0.3} />
                                                            <stop offset="95%" stopColor="var(--diesel-primary)" stopOpacity={0} />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip
                                                        contentStyle={{ borderRadius: '8px', border: '2px solid var(--border)' }}
                                                        formatter={(value, name) => [
                                                            `${value} km/l`,
                                                            name === 'rendimiento' ? 'Rendimiento Real' : 'Ref. Fabricante'
                                                        ]}
                                                    />
                                                    <Legend />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="rendimiento"
                                                        stroke="var(--diesel-primary)"
                                                        strokeWidth={2}
                                                        fill="url(#rendGrad)"
                                                        name="Rendimiento Real"
                                                    />
                                                    {rendFabricante && (
                                                        <ReferenceLine
                                                            y={rendFabricante}
                                                            stroke="#ef4444"
                                                            strokeDasharray="5 5"
                                                            strokeWidth={2}
                                                            label={{ value: `Fabricante: ${rendFabricante}`, position: 'top', fontSize: 11 }}
                                                        />
                                                    )}
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Grafico Tendencia Mensual */}
                                {tendencia_mensual.length > 1 && (
                                    <Card className="border-2" data-testid="chart-tendencia">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <TrendingUp className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                                Tendencia Mensual de Rendimiento
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="h-64">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={tendencia_mensual}>
                                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                        <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                                                        <YAxis tick={{ fontSize: 11 }} />
                                                        <Tooltip contentStyle={{ borderRadius: '8px', border: '2px solid var(--border)' }} />
                                                        <Legend />
                                                        <Line
                                                            type="monotone"
                                                            dataKey="rendimiento"
                                                            stroke="var(--diesel-primary)"
                                                            strokeWidth={3}
                                                            dot={{ r: 5 }}
                                                            name="Rend. Promedio"
                                                        />
                                                        {rendFabricante && (
                                                            <ReferenceLine
                                                                y={rendFabricante}
                                                                stroke="#ef4444"
                                                                strokeDasharray="5 5"
                                                                strokeWidth={2}
                                                            />
                                                        )}
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Grafico Consumo */}
                                <Card className="border-2" data-testid="chart-consumo">
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <Fuel className="h-5 w-5" style={{ color: 'var(--diesel-primary)' }} />
                                            Consumo por Carga (Litros)
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="h-64">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={chartConsumo}>
                                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                    <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip contentStyle={{ borderRadius: '8px', border: '2px solid var(--border)' }} />
                                                    <Legend />
                                                    <Bar dataKey="litros" fill="var(--diesel-primary)" name="Litros" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            </>
                        ) : (
                            <Card className="border-2">
                                <CardContent className="py-16 text-center">
                                    <Gauge className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-xl font-bold">Datos insuficientes</p>
                                    <p className="text-muted-foreground">Se necesitan al menos 2 cargas con rendimiento para generar graficos</p>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Tab: Historial Cargas */}
                    <TabsContent value="cargas">
                        <Card className="border-2" data-testid="tabla-cargas">
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Fecha</TableHead>
                                                <TableHead className="text-right">Litros</TableHead>
                                                <TableHead className="text-right">KM</TableHead>
                                                <TableHead className="text-right">KM Rec.</TableHead>
                                                <TableHead className="text-right">Rendimiento</TableHead>
                                                <TableHead>Tipo</TableHead>
                                                <TableHead>Alerta</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {cargas.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                        Sin registros de carga
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                [...cargas].reverse().map((c) => (
                                                    <TableRow key={c.id}>
                                                        <TableCell className="font-mono text-sm">
                                                            {c.fecha_hora?.slice(0, 16).replace('T', ' ')}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono">{c.litros_cargados}</TableCell>
                                                        <TableCell className="text-right font-mono">{c.kilometraje_actual?.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right font-mono">{c.km_recorridos?.toLocaleString() || '-'}</TableCell>
                                                        <TableCell className="text-right">
                                                            {c.rendimiento_calculado ? (
                                                                <span className={`font-bold ${rendFabricante && c.rendimiento_calculado < rendFabricante * 0.9 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                                    {c.rendimiento_calculado} km/l
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted-foreground">-</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={c.es_carga_inicial ? 'secondary' : c.tipo_carga === 'parcial' ? 'outline' : 'default'}>
                                                                {c.es_carga_inicial ? 'Inicial' : c.tipo_carga}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            {c.alerta_generada && (
                                                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Alertas */}
                    <TabsContent value="alertas">
                        <div className="space-y-4" data-testid="alertas-vehiculo">
                            {alertas.length === 0 ? (
                                <Card className="border-2">
                                    <CardContent className="py-12 text-center">
                                        <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                                        <p className="font-bold">Sin alertas</p>
                                        <p className="text-muted-foreground text-sm">Este vehiculo no tiene alertas registradas</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                alertas.map((alerta) => (
                                    <Card
                                        key={alerta.id}
                                        className={`border-2 ${
                                            alerta.severidad === 'critico' ? 'border-red-500/30 bg-red-500/5' :
                                            alerta.severidad === 'advertencia' ? 'border-amber-500/30 bg-amber-500/5' :
                                            'border-blue-500/30 bg-blue-500/5'
                                        }`}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Badge className={
                                                            alerta.severidad === 'critico' ? 'bg-red-500' :
                                                            alerta.severidad === 'advertencia' ? 'bg-amber-500' : 'bg-blue-500'
                                                        }>
                                                            {alerta.severidad}
                                                        </Badge>
                                                        <Badge variant="outline">{alerta.tipo?.replace(/_/g, ' ')}</Badge>
                                                        <Badge variant="secondary">{alerta.estado}</Badge>
                                                    </div>
                                                    <p className="font-medium">{alerta.mensaje}</p>
                                                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {new Date(alerta.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                                {alerta.estado === 'pendiente' && hasPermission('resolver_alertas') && (
                                                    <Link to="/diesel/alertas">
                                                        <Button size="sm" variant="outline">
                                                            Gestionar
                                                        </Button>
                                                    </Link>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </DieselLayout>
    );
}
